import { memo, useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'

import { InsertEventRecordingAndUploadRecordingFileResponse } from '@crew/apis/event/models/insertEventRecordingAndUploadRecordingFile/response'
import { EventRecording } from '@crew/models/domain'
import { apiBaseUrl } from '@crew/configs/constants'
import { useTranslation } from '@crew/modules/i18n'

import { axiosInstance } from 'apis/axiosInstance'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'

import OutlineVideocam from '~icons/ic/outline-videocam'
import { CrewProgressIndicator } from 'components/elements/crewProgressIndicator/crewProgressIndicator'
import {
  notifyEventRecordingEvent,
  ObjectEventMessage,
} from 'features/app/states/appSlice'
import { NotifyEventType } from 'enums/app'
import { useAppDispatch } from 'states/hooks'

export type CrewMeetingArchiveFileUploaderProps = {
  eventId: string
}

export const CrewMeetingArchiveFileUploader = memo(
  (props: CrewMeetingArchiveFileUploaderProps) => {
    const { t } = useTranslation()
    const toast = useToast()
    const dispatch = useAppDispatch()
    const [showApiErrors] = useShowApiErrors()

    // アップロード中のファイルをキャンセルするためのAbortController。
    // reactiveに反応して欲しくないのでuseRefで管理する。
    const abortControllerRef = useRef<AbortController>()

    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)

    // ファイルをアップロードする関数。
    // 進捗やキャンセルなどをハンドリングするため、axiosを直接操作する。
    const uploadFile = useCallback(
      async (file: File) => {
        // 万一、既にアップロード中のファイルがあればキャンセルする
        abortControllerRef.current?.abort()

        setIsUploading(true)
        setUploadProgress(0)

        try {
          // ファイルサイズの制限をチェック
          if (
            axiosInstance.defaults.maxContentLength &&
            file.size > axiosInstance.defaults.maxContentLength
          ) {
            toast.error(t('message.file.invalidMaxFileSize'))
            return
          }

          const formData = new FormData()
          formData.append('eventId', props.eventId)
          formData.append('file', file)

          const controller = new AbortController()
          abortControllerRef.current = controller

          // TODO: 呼び出し側でaxiosInstanceを直接使用しないように、以下タスクでリファクタ予定
          // https://break-tmc.atlassian.net/browse/CREW-16919
          const result = await axiosInstance.post(
            `${apiBaseUrl()}api/v1/event-recordings/insert-and-upload`,
            formData,
            {
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  )
                  setUploadProgress(percentCompleted)
                }
              },
              signal: controller.signal, // Attach the AbortController signal to Axios request
            }
          )

          const responseData =
            result.data as InsertEventRecordingAndUploadRecordingFileResponse

          if (responseData.eventRecording) {
            // 録画が追加されたことを通知する
            const objectEventRecordingMessage: ObjectEventMessage<EventRecording> =
              {
                eventType: NotifyEventType.Inserted,
                id: responseData.eventRecording.id,
                object: responseData.eventRecording,
              }
            dispatch(notifyEventRecordingEvent(objectEventRecordingMessage))
          }
        } catch (err: any) {
          if (axios.isCancel(err)) {
            // キャンセルの場合はエラー扱いしない
            console.info('Operation canceled')
          } else {
            showApiErrors(err.response)
          }
        } finally {
          setIsUploading(false)
          abortControllerRef.current = undefined
        }
      },
      [dispatch, props.eventId, showApiErrors, t, toast]
    )

    // コンポーネントのアンマウント時にアップロード処理をキャンセルする
    useEffect(() => {
      return () => {
        abortControllerRef.current?.abort()
      }
    }, [])

    const videoFileInputRef = useRef<HTMLInputElement>(null)

    const handleClickUploadVideo = useCallback(() => {
      if (videoFileInputRef.current) {
        videoFileInputRef.current.value = '' // 前回の選択をクリア
        videoFileInputRef.current.click()
      }
    }, [videoFileInputRef])

    const handleChangeVideoFileInput = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        console.log(file)
        if (file) {
          uploadFile(file)
        }
      },
      [uploadFile]
    )

    return (
      <>
        {isUploading ? (
          <div className="w-full absolute bottom-0 left-0">
            <CrewProgressIndicator progress={uploadProgress} />
          </div>
        ) : (
          <CrewLink
            onClick={handleClickUploadVideo}
            title={t('label.uploadRecordedFile')}
            className="flex items-center gap-x-2"
          >
            <OutlineVideocam width={24} height={24} />
            <span>{t('label.uploadRecordedFile')}</span>
          </CrewLink>
        )}
        <input
          type="file"
          ref={videoFileInputRef}
          className="hidden"
          onChange={handleChangeVideoFileInput}
        />
      </>
    )
  }
)
